import {
  ChevronIcon,
  DownloadIcon,
  ExternalIcon,
  SpinnerIcon,
} from "shared/components/Icons";
import { TYPE_BUTTON, TYPE_LINK } from "shared/utils/constants";
import { useRouter } from "next/router";
import cx from "classnames";
import Link from "next/link";
import styles from "./Button.module.css";
import { useAppContext } from "shared/utils/useAppContext";

export const Button = ({
  buttonType = "button",
  children,
  disabled,
  href,
  hrefType,
  isDownload,
  isInverted,
  isBlue,
  isLarge,
  isGhost,
  isExpand,
  loading,
  page,
  type = TYPE_BUTTON,
  ...rest
}) => {
  const { locale } = useRouter();
  const { createInternalHref } = useAppContext();
  const currentScope = useAppContext().scope;

  const isOutOfScope =
    page?.scope &&
    ((currentScope === "Ineo" && !page.scope.ineo) ||
      (currentScope === "Clariah" && !page.scope.clariah));

  const classNames = cx(type === TYPE_LINK ? styles.link : styles.button, {
    [styles.isInverted]: isInverted,
    [styles.isBlue]: isBlue,
    [styles.isGhost]: isGhost,
    [styles.isLarge]: isLarge,
  });

  if (page && page._type) {
    href = page.slug?.current || "";
    hrefType = page._type;
  }

  if (href || href === "") {
    return (
      <Link
        locale={false}
        href={
          typeof href === "object" || href.indexOf("http") === 0
            ? href
            : createInternalHref({ href, locale, type: hrefType, isOutOfScope })
        }
      >
        <a
          className={classNames}
          download={isDownload}
          target={isOutOfScope ? "_blank" : "_self"}
          {...rest}
          disabled={disabled}
        >
          <ButtonInner
            children={children}
            hasChevron={type === TYPE_LINK && typeof isExpand === "undefined"}
            href={href}
            isDownload={isDownload}
            isOutOfScope={isOutOfScope}
            isExpand={isExpand}
          />
        </a>
      </Link>
    );
  }

  return (
    <button
      className={classNames}
      disabled={disabled || loading}
      type={buttonType}
      {...rest}
    >
      <ButtonInner
        children={children}
        hasChevron={type === TYPE_LINK && typeof isExpand === "undefined"}
        href={href}
        isDownload={isDownload}
        loading={loading}
        isOutOfScope={isOutOfScope}
        isExpand={isExpand}
      />
    </button>
  );
};

const ButtonInner = ({
  children,
  loading,
  href,
  isDownload,
  hasChevron,
  isOutOfScope,
  isExpand,
}) => {
  const isExternal =
    isOutOfScope ||
    (href &&
      href.indexOf("http") === 0 &&
      href.indexOf("cdn.sanity.io") === -1);

  return (
    <span className={styles.wrapper}>
      {isDownload && <DownloadIcon />}
      <span className={styles.label}>
        {isExpand === true ? "+" : isExpand === false ? "-" : null} {children}
      </span>
      {loading ? (
        <span className={styles.loading}>
          <SpinnerIcon />
        </span>
      ) : isExternal ? (
        <ExternalIcon />
      ) : isDownload ? null : hasChevron ? (
        <span style={{ transform: `rotate(-90deg) translateX(-2px)` }}>
          <ChevronIcon />
        </span>
      ) : null}
    </span>
  );
};

export const createButtonProps = ({
  href,
  label,
  page,
  isDownload,
  file,
  type,
}) => ({
  href: href || file?.url,
  label: label || href || file?.name,
  isDownload,
  page,
  file,
  type,
});
